// Il dizionario di una lingua: dal testo italiano alla traduzione.
//
// Due tipi di voce, nello stesso JSON:
//  - fisse: "Cerca": "Search";
//  - con segnaposto {nome}: "Online dal {data}": "Online since {data}".
//    Servono a t('Online dal {data}', { data }) e a riconoscere il testo già
//    composto che arriva nella pagina ('Online dal 12/03' → 'Online since
//    12/03'), così una frase con un valore dentro non va riscritta nel
//    codice. I valori presi dalla frase si traducono a loro volta, se il
//    dizionario li conosce.
//
// Stessa logica di lib/l10n/l10n_dictionary.dart in newarc-platform.

const PLACEHOLDER = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
const LETTER = /[A-Za-zÀ-ÿ]/g;
const CACHE_LIMIT = 8000;

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// null se la voce non ha segnaposto, ne ha due attaccati (ambigua) o la parte
// fissa è troppo generica per riconoscere una frase ('{a} e {b}').
function parseTemplate(rawKey, rawValue) {
  const key = rawKey.trim();
  const value = rawValue.trim();
  const matches = [...key.matchAll(PLACEHOLDER)];
  if (!matches.length) return null;
  const literals = [];
  const names = [];
  let pos = 0;
  for (const m of matches) {
    const literal = key.slice(pos, m.index);
    if (!literal && names.length) return null;
    literals.push(literal);
    names.push(m[1]);
    pos = m.index + m[0].length;
  }
  literals.push(key.slice(pos));
  const fixed = literals.join('');
  const head = literals[0];
  const space = head.indexOf(' ');
  const indexToken = space > 0 ? head.slice(0, space) : null;
  // Abbastanza lettere fisse, o una parola intera in testa ('Ciao {nome}').
  if ((fixed.match(LETTER) || []).length < 5 && (indexToken || '').replace(/[^A-Za-zÀ-ÿ]/g, '').length < 4) return null;
  let source = '^';
  names.forEach((_, i) => {
    source += escapeRegExp(literals[i]) + (i === names.length - 1 ? '([\\s\\S]*)' : '([\\s\\S]*?)');
  });
  source += escapeRegExp(literals[literals.length - 1]) + '$';
  const probe = literals.reduce((a, b) => (b.length > a.length ? b : a), '');
  return { pattern: new RegExp(source), names, value, indexToken, probe };
}

export class Dictionary {
  constructor(entries = {}) {
    this.exact = new Map();
    this.byToken = new Map();
    this.unindexed = [];
    this.cache = new Map();
    for (const [key, value] of Object.entries(entries)) {
      if (typeof key !== 'string' || typeof value !== 'string' || !value.trim()) continue;
      this.exact.set(key, value);
      const template = parseTemplate(key, value);
      if (!template) continue;
      if (template.indexToken === null) this.unindexed.push(template);
      else {
        if (!this.byToken.has(template.indexToken)) this.byToken.set(template.indexToken, []);
        this.byToken.get(template.indexToken).push(template);
      }
    }
  }

  get isEmpty() {
    return this.exact.size === 0;
  }

  // La traduzione di text, o text stesso se il dizionario non ce l'ha. Spazi
  // ai lati e due punti finali non contano: li rimette.
  translate(text) {
    if (this.isEmpty || typeof text !== 'string' || !text) return text;
    const exact = this.exact.get(text);
    if (exact !== undefined) return exact;
    const cached = this.cache.get(text);
    if (cached !== undefined) return cached;
    const result = this.lookup(text, true) ?? text;
    if (this.cache.size >= CACHE_LIMIT) this.cache.clear();
    this.cache.set(text, result);
    return result;
  }

  lookup(text, useTemplates) {
    const trimmed = text.trim();
    if (!trimmed) return null;
    let found = trimmed === text ? undefined : this.exact.get(trimmed);
    let suffix = '';
    if (found === undefined && trimmed.length > 1 && trimmed.endsWith(':')) {
      found = this.exact.get(trimmed.slice(0, -1).trimEnd());
      if (found !== undefined) suffix = ':';
    }
    if (found === undefined && useTemplates) found = this.matchTemplate(trimmed);
    if (found === undefined || found === null) return null;
    const start = text.indexOf(trimmed);
    return text.slice(0, start) + found + suffix + text.slice(start + trimmed.length);
  }

  matchTemplate(text) {
    const space = text.indexOf(' ');
    const token = space < 0 ? text : text.slice(0, space);
    const candidates = this.byToken.get(token) || [];
    for (const list of [candidates, this.unindexed]) {
      for (const t of list) {
        if (!text.includes(t.probe)) continue;
        const m = t.pattern.exec(text);
        if (!m) continue;
        let out = t.value;
        t.names.forEach((name, i) => {
          out = out.split(`{${name}}`).join(this.translateValue(m[i + 1]));
        });
        return out;
      }
    }
    return null;
  }

  // Un valore preso da una frase: solo voci fisse (una frase dentro una frase
  // finirebbe in un giro senza fine).
  translateValue(value) {
    const exact = this.exact.get(value);
    if (exact !== undefined) return exact;
    return this.lookup(value, false) ?? value;
  }
}

export const EMPTY_DICTIONARY = new Dictionary({});
